import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";

// Import page components
import ProfessionalProfile from "../(User)Profile_Final/ProfilePage.jsx";
import Mo3tiDocumentsPage from "../(User)SelfService";
import Mo3tiAttendancePage from "../(User)attendance_page";
import Mo3tiLeavePage from "../(User)leave_Managment";
import Mo3tiSettingsPage from "../(All) settings";
import Mo3tiSalaryPage from "../(User)salary_Final";

import Calendar from "../(All) Calendar/index.jsx";
import MessengerPage from "../(All) messenger";
import SidebarWithTopbar from "../(All) Sidebar/SidebarWithTopbar";
import { mo3tiThemes } from "../../components/mo3tiThemes.js";
import ViewTypeSwitcher from "../../components/ViewTypeSwitcher";
import EscalationPage from "../(User)EscalationComplaints";
import GroupsManagement from "../(user) GroupsManagement";

// Local modules
import { translations } from "./locales";
import { getNavItems } from "./config/navItems";
import ClientModuleGuard, { SELF_SERVICE_PAGE_MODULES, filterNavByClientModules } from "../../components/ClientModuleGuard.jsx";
import { useRTLDocument } from "./hooks/useRTLDocument";
import { useViewType } from "./hooks/useViewType";
import { useEmployeeProfileData } from "./hooks/useEmployeeProfileData";
import ThemeToggleButton from "./components/ThemeToggleButton";
import LanguageToggleButton from "./components/LanguageToggleButton";
import {
  getContainerStyles,
  getBackgroundOverlayStyles,
  getDecorativeContainerStyles,
  getCircle1Styles,
  getCircle2Styles,
  getGlobalStyles,
} from "./styles/userProfileStyles";

// ===============================================
// MAIN COMPONENT - المكون الرئيسي
// ===============================================
const Mo3tiUserProfile = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout, isClientModuleEnabled } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language, toggleLanguage } = useContext(LanguageContext);

  // Language and direction
  const isRTL = language === 'ar';
  const t = translations[language];

  // Apply RTL to document
  useRTLDocument(isRTL, language);

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Current page state
  const [currentPage, setCurrentPage] = useState('professionalProfile');

  // View type state - unified key with DashboardManager
  const { viewType, handleViewTypeChange, hasAdminLevel } = useViewType(profile);

  // Reset currentPage when viewType changes
  useEffect(() => {
    setCurrentPage('professionalProfile');
  }, [viewType]);

  // Keep the sidebar highlight in sync with the URL — deep links (e.g.
  // notification navigation to /userprofilemanager/escalation) land here
  // without going through handlePageChange.
  const location = useLocation();
  useEffect(() => {
    const segment = location.pathname
      .replace(/^\/userprofilemanager\/?/i, '')
      .split('/')[0];
    setCurrentPage(segment || 'professionalProfile');
  }, [location.pathname]);

  // ============ DATA CONFIGURATION ============
  const currentTheme = isDarkMode ? mo3tiThemes.dark : mo3tiThemes.light;

  // Navigation menu items with translations
  // Per-client module switches (superadmin): pages whose module is off for
  // the client are hidden fully here too — a manager browsing as a user is
  // still inside the same client.
  const navItems = filterNavByClientModules(getNavItems(t), isClientModuleEnabled);
  const guard = (page, element) => (
    <ClientModuleGuard module={SELF_SERVICE_PAGE_MODULES[page] || null} redirectTo="/userprofilemanager/professionalProfile">
      {element}
    </ClientModuleGuard>
  );

  // Employee data — profile (from /users/me) is source of truth;
  // localStorage serves as an immediate fallback (populated at login).
  const employeeData = useEmployeeProfileData(profile);

  // ============ EVENT HANDLERS ============
  const handleLogout = () => {
    try {
      logout();
      try {
        localStorage.removeItem('nav_admin');
        localStorage.removeItem('nav_manager');
        localStorage.removeItem('nav_employee');
      } catch (e) { }
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      // Clear the saved page when switching
      localStorage.removeItem('adminUserProfilePage');
      // Navigate to dashboard root
      navigate("/DashboardManager/dashboard");
    } catch (error) {
      console.error("switch error:", error);
      navigate("/userprofilemanager");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/userprofilemanager/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  // ============ CREATE PAGE PROPS ============
  const pageProps = {
    employeeData,
    theme: currentTheme,
    isDarkMode
  };

  // ============ STYLES ============
  const containerStyles = getContainerStyles(currentTheme, isRTL);
  const backgroundOverlayStyles = getBackgroundOverlayStyles(currentTheme);
  const decorativeContainerStyles = getDecorativeContainerStyles(currentTheme);
  const circle1Styles = getCircle1Styles(currentTheme);
  const circle2Styles = getCircle2Styles(currentTheme);
  const globalStyles = getGlobalStyles(currentTheme, isRTL, sidebarOpen);

  // ============ MAIN RENDER ============
  return (
    <div style={containerStyles}>
      {/* Background Elements */}
      <div style={backgroundOverlayStyles} />
      <div style={decorativeContainerStyles}>
        <div style={circle1Styles} />
        <div style={circle2Styles} />
      </div>

      {/* Sidebar with Topbar */}
      <SidebarWithTopbar
        navItems={navItems}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        user={user}
        notifications={notifications}
        handleLogout={handleLogout}
        handleSwitchManager={handleSwitchManager}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        themeToggle={<ThemeToggleButton theme={theme} toggleTheme={toggleTheme} t={t} />}
        languageToggle={<LanguageToggleButton theme={theme} language={language} toggleLanguage={toggleLanguage} />}
        isRTL={isRTL}
        viewModeSwitcher={
          <ViewTypeSwitcher
            viewType={viewType}
            onSelect={(type) => {
              // keep page navigation/logic centralized here while storage writes happen in the component
              handleViewTypeChange(type);
            }}
            hasAdminLevel={() => hasAdminLevel()}
            role={profile?.role || localStorage.getItem('role')}
          />
        }
      >
        <div style={{
          padding: "0",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: currentPage === "messages" ? 0 : 40,
        }}>
          <Routes>
            <Route path="/" element={<ProfessionalProfile />} />
            <Route path="/professionalProfile" element={<ProfessionalProfile />} />
            <Route path="/attendance" element={guard("attendance", <Mo3tiAttendancePage {...pageProps} />)} />
            <Route path="/salary" element={guard("salary", <Mo3tiSalaryPage {...pageProps} />)} />
            <Route path="/employee-services" element={guard("employee-services", <Mo3tiDocumentsPage {...pageProps} />)} />
            <Route path="/calendar" element={guard("calendar", <Calendar {...pageProps} />)} />
            <Route path="/messages" element={guard("messages", <MessengerPage {...pageProps} />)} />
            <Route path="/leave-management" element={guard("leave-management", <Mo3tiLeavePage {...pageProps} />)} />
            <Route path="/escalation" element={guard("escalation", <EscalationPage {...pageProps} />)} />
            <Route path="/groups" element={guard("groups", <GroupsManagement />)} />
            <Route path="/settings" element={<Mo3tiSettingsPage {...pageProps} toggleTheme={toggleTheme} />} />
          </Routes>
        </div>
      </SidebarWithTopbar>

      {/* Global Styles */}
      <style>{globalStyles}</style>
    </div>
  );
};

export default Mo3tiUserProfile;
